import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { PaymentDeviceDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { PaymentDeviceDtoOut } from '@/models/out/PaymentDeviceDtoOut';
import { MANAGEMENT_DEVICES_URL, paymentDeviceErrorMessage } from '@/helpers/paymentDevices';
import usePaymentDeviceApi from '../usePaymentDeviceApi';
import { DialogActions, ErrorLine, Field, NoteField, inputClassName } from './dialogParts';

const DEVICE_NUMBER_MAX_LENGTH = 50;

interface DeviceFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** null = a new device. */
  device: PaymentDeviceDtoIn | null;
}

/**
 * Adds a terminal to the inventory or edits one. Its status is not edited here: it changes only
 * through the actions (assign, hand over, return...), so the device's history stays true. There is
 * no location: every terminal works in Israel and in the US.
 */
const DeviceFormDialog = ({ open, onClose, device }: DeviceFormDialogProps) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();

  const [deviceNumber, setDeviceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeviceNumber(device?.deviceNumber ?? '');
    setNotes(device?.notes ?? '');
    setError(null);
  }, [open, device]);

  const handleSubmit = async () => {
    const number = deviceNumber.trim();
    if (!number) {
      setError(t('paymentDevices.dialogs.deviceNumberRequired'));
      return;
    }
    if (number.length > DEVICE_NUMBER_MAX_LENGTH) {
      setError(t('paymentDevices.dialogs.deviceNumberTooLong'));
      return;
    }

    const body: PaymentDeviceDtoOut = { deviceNumber: number, notes: notes.trim() || null };
    setError(null);
    setIsSubmitting(true);
    try {
      if (device) {
        await api.put<PaymentDeviceDtoIn, PaymentDeviceDtoOut>(`${MANAGEMENT_DEVICES_URL}/${device.id}`, body);
      } else {
        await api.post<PaymentDeviceDtoIn, PaymentDeviceDtoOut>(MANAGEMENT_DEVICES_URL, body);
      }
      onClose();
    } catch (err) {
      setError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={device ? t('paymentDevices.dialogs.deviceEditTitle', { number: device.deviceNumber }) : t('paymentDevices.dialogs.deviceCreateTitle')}
      maxWidth="520px"
      fitContent
    >
      <div className="flex flex-col gap-4 p-4">
        <Field id="pd-number" label={t('paymentDevices.dialogs.deviceNumber')}>
          <input
            id="pd-number"
            type="text"
            dir="ltr"
            maxLength={DEVICE_NUMBER_MAX_LENGTH}
            className={inputClassName}
            value={deviceNumber}
            onChange={(e) => setDeviceNumber(e.target.value)}
          />
        </Field>
        <NoteField id="pd-notes" label={t('paymentDevices.dialogs.notesOptional')} value={notes} onChange={setNotes} />
        <ErrorLine error={error} />
        <DialogActions onCancel={onClose} onConfirm={handleSubmit} isSubmitting={isSubmitting} />
      </div>
    </MainDialogLayout>
  );
};

export default DeviceFormDialog;
